import { DOCUMENTS_API } from '@Constants/backendConstants';
import {
    CompiledDocumentPreview,
    DocumentCompileRequest,
    DocumentsContext,
    RenderPageRequest,
    RenderResult,
    WorkerState,
} from '@Contexts/documents/DocumentsContext';
import { useLogger, useToken } from '@synqo/frontend-component-library';
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import {
    type DocumentsWorkerRequest,
    DocumentsWorkerRequestKind,
    type DocumentsWorkerResponse,
    DocumentsWorkerResponseKind,
} from './types';

interface PendingRequest {
    resolve: (value: CompiledDocumentPreview | RenderResult) => void;
    reject: (error: Error) => void;
}

const RENDER_TIMEOUT_MS = 30_000;

export const DocumentsProvider: FC<PropsWithChildren> = ({ children }) => {
    const nonce = useRef(0);
    const pendingRequests = useRef<Record<number, PendingRequest>>({});
    const [state, setState] = useState<WorkerState>(WorkerState.Initializing);
    const worker = useRef<SharedWorker | undefined>(undefined);
    const { getToken } = useToken();
    const { error } = useLogger();

    useEffect(() => {
        const sharedWorker = new SharedWorker(new URL('./documents.worker.ts', import.meta.url), { type: 'module' });

        // Resolves/rejects the promise waiting on this nonce and clears it. Late responses whose
        // nonce is no longer pending (e.g. the request already timed out) are silently ignored.
        const settlePending = (nonce: number, settle: (request: PendingRequest) => void) => {
            const callback = pendingRequests.current[nonce];
            if (callback === undefined) {
                return;
            }
            delete pendingRequests.current[nonce];
            settle(callback);
        };

        sharedWorker.port.onmessage = (event: MessageEvent<DocumentsWorkerResponse>) => {
            switch (event.data.kind) {
                case DocumentsWorkerResponseKind.Compiled: {
                    const { nonce, documentToken, pages } = event.data;
                    settlePending(nonce, (request) => request.resolve({ documentToken, pages }));
                    break;
                }
                case DocumentsWorkerResponseKind.Page: {
                    const { nonce, preview } = event.data;
                    const blob = new Blob([preview], { type: 'image/png' });
                    settlePending(nonce, (request) => request.resolve({ result: blob }));
                    break;
                }
                case DocumentsWorkerResponseKind.Error: {
                    const { nonce, message } = event.data;
                    error(`Documents worker request failed: ${message}`);
                    settlePending(nonce, (request) => request.reject(new Error(message)));
                    break;
                }
                case DocumentsWorkerResponseKind.Ready: {
                    setState(WorkerState.Ready);
                    break;
                }
                case DocumentsWorkerResponseKind.Broken: {
                    setState(WorkerState.Broken);
                    break;
                }
            }
        };
        sharedWorker.port.onmessageerror = (event) => {
            error(`Received error from worker: ${event.data}`);
        };
        sharedWorker.port.start();

        worker.current = sharedWorker;

        // Best-effort cleanup for documents this tab compiled but never explicitly released (e.g.
        // the tab was closed while a preview was still open): let the worker release its share of
        // compiledDocuments instead of relying solely on each hook's own unmount-time Release call.
        const sendDisconnect = () => {
            worker.current?.port.postMessage({
                kind: DocumentsWorkerRequestKind.Disconnect,
            } satisfies DocumentsWorkerRequest);
        };
        const handlePageHide = (event: PageTransitionEvent) => {
            if (event.persisted) {
                // The page is going into the back-forward cache rather than actually unloading,
                // and can come back with this exact SharedWorker port still connected. Leave the
                // compiled documents alive so a subsequent back-navigation doesn't hit a
                // "not compiled (anymore)" error for pages that were already rendered.
                return;
            }
            sendDisconnect();
        };
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            if (worker.current) {
                sendDisconnect();
                worker.current.port.close();
                worker.current = undefined;
            }
        };
    }, [error]);

    const registerPending = useCallback(
        <T extends CompiledDocumentPreview | RenderResult>(myNonce: number): Promise<T> => {
            return new Promise<T>((resolve, reject) => {
                const timeout = setTimeout(() => {
                    if (pendingRequests.current[myNonce] !== undefined) {
                        delete pendingRequests.current[myNonce];
                        reject(new Error(`Document render timed out after ${RENDER_TIMEOUT_MS}ms`));
                    }
                }, RENDER_TIMEOUT_MS);

                pendingRequests.current[myNonce] = {
                    resolve: (value) => {
                        clearTimeout(timeout);
                        resolve(value as T);
                    },
                    reject: (renderError) => {
                        clearTimeout(timeout);
                        reject(renderError);
                    },
                };
            });
        },
        [],
    );

    // Sends a nonce-bearing request to the worker, or rejects its pending promise right away if the
    // worker is gone (e.g. after unmount) instead of leaving the caller to wait for the timeout.
    const postToWorker = useCallback((message: DocumentsWorkerRequest, myNonce: number) => {
        if (worker.current === undefined) {
            pendingRequests.current[myNonce]?.reject(new Error('Documents worker is not available'));
            return;
        }
        worker.current.port.postMessage(message);
    }, []);

    const compile = useCallback(
        async (request: DocumentCompileRequest): Promise<CompiledDocumentPreview> => {
            const authToken = await getToken();
            const myNonce = nonce.current++;
            const promise = registerPending<CompiledDocumentPreview>(myNonce);

            postToWorker(
                {
                    ...request,
                    kind: DocumentsWorkerRequestKind.Compile,
                    nonce: myNonce,
                    authToken,
                    documentsApi: DOCUMENTS_API,
                },
                myNonce,
            );

            return promise;
        },
        [getToken, registerPending, postToWorker],
    );

    const renderPage = useCallback(
        (request: RenderPageRequest): Promise<RenderResult> => {
            const myNonce = nonce.current++;
            const promise = registerPending<RenderResult>(myNonce);

            postToWorker(
                {
                    ...request,
                    kind: DocumentsWorkerRequestKind.RenderPage,
                    nonce: myNonce,
                },
                myNonce,
            );

            return promise;
        },
        [registerPending, postToWorker],
    );

    const release = useCallback((documentToken: number) => {
        const message: DocumentsWorkerRequest = { kind: DocumentsWorkerRequestKind.Release, documentToken };
        worker.current?.port.postMessage(message);
    }, []);

    return (
        <DocumentsContext.Provider value={{ state, compile, renderPage, release }}>
            {children}
        </DocumentsContext.Provider>
    );
};
